const internals = {};

module.exports = { getInternalLinkPath, getInternalAppPath, openExternalUrl, isGroupAppPath, isGroupChatAppPath, isProfileAppPath, internals };

function getInternalAppPath(url) {

    return internals.toSafeAppPath(getInternalLinkPath(url));
}

function getInternalLinkPath(url) {

    if (typeof url !== 'string') {
        return null;
    }

    const trimmed = url.trim();

    if (!trimmed || /[\\\u0000-\u0020]/.test(trimmed)) {
        return null;
    }

    try {
        const isRelativeAppPath = trimmed.startsWith('/app/');
        const parsedUrl = isRelativeAppPath
            ? new URL(trimmed, 'https://nearpeer.net')
            : new URL(internals.ensureProtocol(trimmed));

        if (!['http:', 'https:'].includes(parsedUrl.protocol) || parsedUrl.username || parsedUrl.password ||
            (!isRelativeAppPath && !internals.isInternalAppHostname(parsedUrl.hostname))) {
            return null;
        }

        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}` || '/';
    }
    catch (err) {
        return null;
    }
}

function openExternalUrl(url) {

    if (!url) {
        return null;
    }

    const InAppBrowser = window.cordova?.InAppBrowser;

    if (InAppBrowser) {
        return InAppBrowser.open(url, '_system');
    }

    return window.open(url, '_blank', 'noopener,noreferrer');
}

function isGroupAppPath(path) {

    try {
        const pathname = decodeURI(String(path || '').split(/[?#]/, 1)[0]);

        return pathname !== '/app/classes/create' && pathname !== '/app/classes/create/' &&
            /^\/app\/classes\/(?:chat\/[^/]+|announcement\/chat\/[^/]+|[^/]+)\/?$/.test(pathname);
    }
    catch (err) {
        return false;
    }
}

function isProfileAppPath(path) {

    try {
        const pathname = decodeURI(String(path || '').split(/[?#]/, 1)[0]).replace(/\/$/, '');

        return !['/app/profile/edit', '/app/profile/edit/interests', '/app/profile/change-password'].includes(pathname) &&
            /^\/app\/profile(?:\/|$)/.test(pathname);
    }
    catch (err) {
        return String(path || '').startsWith('/app/profile/');
    }
}

function isGroupChatAppPath(path) {

    try {
        return /^\/app\/classes\/(?:chat|announcement\/chat)\//.test(decodeURI(String(path || '')));
    }
    catch (err) {
        return false;
    }
}

internals.ensureProtocol = (url) => {

    if (url.match(/^[a-z][a-z\d+.-]*:\/\//i)) {
        return url;
    }

    if (url.startsWith('//')) {
        return `https:${url}`;
    }

    return `http://${url}`;
};

internals.normalizeHostname = (hostname) => {

    const normalizedHostname = String(hostname || '').toLowerCase().replace(/\.$/, '');

    // These deployed www hosts are aliases of their canonical environment.
    return normalizedHostname.replace(/^www\.(?=(?:teams\.|staging\.)?nearpeer\.net$)/, '');
};

internals.getRegisteredAppHostnames = () => {

    const configuredUrls = [
        process.env.API_HOST,
        process.env.REACT_APP_AUTH0_LOGIN_CALLBACK_URL,
        process.env.REACT_APP_AUTH0_SIGNUP_CALLBACK_URL,
        process.env.REACT_APP_KEYCLOAK_LOGIN_CALLBACK_URL,
        process.env.REACT_APP_KEYCLOAK_SIGNUP_CALLBACK_URL
    ];

    if (typeof window !== 'undefined' && !window.cordova && window.location?.hostname) {
        configuredUrls.unshift(window.location.href);
    }

    // A WebView's file/localhost origin is not its deployment environment.
    // Use one authoritative host, rather than merging different environments.
    for (const configuredUrl of configuredUrls) {

        if (!configuredUrl) {
            continue;
        }

        try {
            const { hostname, protocol } = new URL(internals.ensureProtocol(String(configuredUrl).replaceAll('"', '')));

            if (!hostname || !['http:', 'https:'].includes(protocol)) {
                continue;
            }

            return [hostname];
        }
        catch (err) {}
    }

    return [];
};

internals.isInternalAppHostname = (hostname) => {

    const normalizedHostname = internals.normalizeHostname(hostname);

    return internals.getRegisteredAppHostnames().some((registeredHostname) => {

        return internals.normalizeHostname(registeredHostname) === normalizedHostname;
    });
};

internals.toSafeAppPath = (path) => {

    if (typeof path !== 'string' || path.includes('\\')) {
        return null;
    }

    const withoutHash = path.split('#', 1)[0];
    const pathname = withoutHash.split('?', 1)[0];

    if (!pathname.startsWith('/app/') || pathname === '/app/loader') {
        return null;
    }

    const searchIndex = withoutHash.indexOf('?');
    const search = searchIndex === -1 ? '' : withoutHash.slice(searchIndex);

    return `${pathname}${search}`;
};
